import { updateIntl } from "react-intl-redux";
import { createModel } from "@rematch/core";
import { Connector } from "checkout/ts/Connector";
import { CheckoutParametersError } from "checkout/ts/errors/CheckoutParametersError";
import { getLocaleData } from "checkout/ts/locale";
import { mapInlineElementToFieldName, SplitCheckoutConnector } from "checkout/ts/SplitCheckoutConnector";
import { AUTO_CLOSE_TIMEOUT_DEFAULT, FORM_CHECKOUT_NAME } from "common/constants";
import { getLocale } from "common/locale";
import { CheckoutUpdateOptions, CheckoutUpdateParameters } from "common/Messages";
import { CheckoutParams, CheckoutType, InlineSplitElement } from "common/types";
import { updateParamsToCheckoutParams } from "common/utils/parameters";
import { formatParameters, schema } from "common/validation/schema";
import { get, isEqual, merge, omit, pick } from "lodash";
import { change, getFormValues } from "redux-form";

import { Dispatch, StateShape } from "../store";

import { SuccessfulStatuses } from "./checkout";

export type ApplicationParams = {
    appId?: string;
    checkout?: CheckoutType;
    params?: CheckoutParams;
};

export type ApplicationModelStateShape = {
    open?: boolean;

    params?: ApplicationParams;
    globalError?: Error;
    connector?: Connector;
    splitCheckoutConnector?: SplitCheckoutConnector;
    origin?: string;

    /**
     * In case of validation error when the field is not shown, override the required state to show it to the user
     */
    invalidFields?: {
        email?: { showField: boolean; error: string };
        name?: { showField: boolean; error: string };
        nameKana?: { showField: boolean; error: string };
        phoneNumber?: { showField: boolean; error: string };
        line1?: { showField: boolean; error: string };
        line2?: { showField: boolean; error: string };
        state?: { showField: boolean; error: string };
        city?: { showField: boolean; error: string };
        country?: { showField: boolean; error: string };
        zip?: { showField: boolean; error: string };
        cardNumber?: { showField: boolean; error: string };
    };
};

const initialState: ApplicationModelStateShape = {
    open: false,
    connector: null,
    globalError: null,
    params: {
        params: {
            locale: "auto",
        } as CheckoutParams,
    },

    invalidFields: {},
};

const model = {
    state: initialState,

    reducers: {
        setConnector: (state: ApplicationModelStateShape, { connector }: { connector: Connector }) => ({
            ...state,
            connector,
        }),
        setSplitCheckoutConnector: (
            state: ApplicationModelStateShape,
            { connector }: { connector: SplitCheckoutConnector }
        ) => ({
            ...state,
            splitCheckoutConnector: connector,
        }),
        setError: (state: ApplicationModelStateShape, { error }: { error: Error }) => ({
            ...state,
            globalError: error,
        }),
        setParams: (state: ApplicationModelStateShape, { params }: { params: ApplicationParams }) => ({
            ...state,
            params,
        }),

        updateParams: (state: ApplicationModelStateShape, { overrides }: { overrides: Partial<CheckoutParams> }) => ({
            ...state,
            params: merge({}, state.params, { params: overrides }),
        }),

        open: (state: ApplicationModelStateShape) => ({ ...state, open: true }),
        setClose: (state: ApplicationModelStateShape) => ({ ...state, open: false }),

        clearItem: (state: ApplicationModelStateShape) => ({ ...state, data: null }),
        clearError: (state: ApplicationModelStateShape) => ({ ...state, error: null }),

        setInvalidFields: (
            state: ApplicationModelStateShape,
            { invalidFields }: { invalidFields: { name: string; error: string }[] }
        ) => ({
            ...state,
            invalidFields: {
                ...state.invalidFields,
                ...invalidFields.reduce((acc, { name, error }) => ({ ...acc, [name]: { showField: true, error } }), {}),
            },
        }),

        clearInvalidField: (state: ApplicationModelStateShape, { name }: { name: string }) => ({
            ...state,
            invalidFields: {
                ...state.invalidFields,
                [name]: null,
            },
        }),

        clearInvalidFields: (state: ApplicationModelStateShape) => ({
            ...state,
            invalidFields: initialState.invalidFields,
        }),

        clearInvalidFieldError: (state: ApplicationModelStateShape, { name }: { name: string }) => ({
            ...state,
            invalidFieldErrors: {
                ...state.invalidFields,
                [name]: {
                    ...state.invalidFields[name],
                    error: null,
                },
            },
        }),
    },

    effects: (dispatch: Dispatch) => ({
        connectOrigin: async (): Promise<Connector> => {
            const { application: self } = dispatch;

            const connector = new Connector();
            self.setConnector({ connector });

            try {
                const connectorParams = (await connector.connect()) as ApplicationParams;

                const locale = getLocale(connectorParams.params.locale);
                await dispatch(updateIntl(getLocaleData(locale)));

                const { params: buttonParams, ...appParams } = connectorParams;
                const params = formatParameters({ ...buttonParams, ...appParams }, { hasLegacyPlanParameter: true });

                try {
                    schema.validateSync(params);
                } catch (errors) {
                    throw new CheckoutParametersError(errors);
                }

                const appParamsKeys = Object.keys(appParams);
                const formattedParams = omit(params, appParamsKeys);
                self.setParams({
                    params: { ...pick(params, appParamsKeys), params: formattedParams as CheckoutParams },
                });

                if (connectorParams.params.inlineSplitId && connectorParams.params.inlineSplitElements) {
                    self.connectSplitCheckout({
                        splitId: params.inlineSplitId,
                        elements: Array.isArray(params.inlineSplitElements)
                            ? params.inlineSplitElements
                            : [params.inlineSplitElements],
                    });
                }

                return connector;
            } catch (error) {
                self.setError({ error });
            } finally {
                connector.emitter.emit("checkout:opened");
                self.open();
            }
        },

        connectSplitCheckout: async ({
            splitId,
            elements,
        }: {
            splitId: string;
            elements: InlineSplitElement[];
        }): Promise<SplitCheckoutConnector | void> => {
            const { application: self } = dispatch;

            const connector = new SplitCheckoutConnector(splitId, elements);
            self.setSplitCheckoutConnector({ connector });
            try {
                await connector.connect();

                return connector;
            } catch (error) {
                self.setError({ error });
            }
        },

        clientUpdateParameters: async (
            { overrides, options }: { overrides: CheckoutUpdateParameters; options?: CheckoutUpdateOptions },
            state: StateShape
        ) => {
            const { application: self, checkout } = dispatch;

            self.updateParams({ overrides: updateParamsToCheckoutParams(overrides) });

            const { forceUpdate = false } = options || {};
            const formValues = getFormValues(FORM_CHECKOUT_NAME)(state);
            const updateWhenUnchanged = <T>(field: string, override: T, initialValue?: T) => {
                if (override === undefined) {
                    return;
                }

                if (forceUpdate || isEqual(get(formValues, field), initialValue)) {
                    dispatch(change(FORM_CHECKOUT_NAME, field, override));
                }
            };

            const params = state.application.params.params;
            updateWhenUnchanged("email", overrides.email, params.email);
            updateWhenUnchanged("name", overrides.name, params.customerName);
            updateWhenUnchanged("nameKana", overrides.nameKana, params.customerNameKana);
            updateWhenUnchanged("data.line1", overrides.address?.line1, params.shippingAddressLine1);
            updateWhenUnchanged("data.line2", overrides.address?.line2, params.shippingAddressLine2);
            updateWhenUnchanged("data.city", overrides.address?.city, params.shippingAddressCity);
            updateWhenUnchanged("data.state", overrides.address?.state, params.shippingAddressState);
            updateWhenUnchanged("data.zip", overrides.address?.zip, params.shippingAddressZip);
            updateWhenUnchanged(
                "data.country",
                overrides.address?.countryCode,
                params.shippingAddressCountryCode?.toUpperCase()
            );
            updateWhenUnchanged(
                "data.phoneNumber",
                overrides.phoneNumber,
                params.phoneNumber
                    ? {
                          countryCode: "81",
                          localNumber: params.phoneNumber,
                      }
                    : null
            );

            if (params?.inline && params?.inlineSplitId) {
                Object.values(InlineSplitElement).forEach((element) => {
                    const fieldName = mapInlineElementToFieldName(element);

                    if (fieldName === InlineSplitElement.DATA) {
                        return;
                    }

                    updateWhenUnchanged(fieldName, get(overrides, fieldName), params[fieldName]);

                    if (element === InlineSplitElement.DATA_CARD_INSTALLMENTS) {
                        checkout.setUserInstallmentCount({ userInstallmentCount: overrides[element] });
                    }
                });
            }
        },

        autoCloseApplication: async (_?, state?: StateShape) => {
            const { application: self } = dispatch;
            const { autoClose = false, autoCloseOnError = false } = state.application.params.params;
            const { data, processed } = state.checkout;

            if (!autoClose && !autoCloseOnError) {
                return; // The application should not auto close from the parameter. Do not close.
            }
            if (!processed || (data?.["status"] && !SuccessfulStatuses.includes(data["status"]) && !autoCloseOnError)) {
                return;
            }

            await new Promise((resolve) => setTimeout(resolve, AUTO_CLOSE_TIMEOUT_DEFAULT)); // Delay auto close
            await self.close();
        },

        close: async (_?, state?: StateShape) => {
            const { application: self } = dispatch;
            const {
                connector,
                params: {
                    params: { hasBeforeClosingCallback },
                },
            } = state.application;
            const { failedSubscriptionId } = state.checkout;

            // Connector connection may have been lost when the parent page is closed. Do not assume the connector
            if (connector) {
                const shouldClose = await (async () => {
                    if (!hasBeforeClosingCallback) {
                        return true;
                    }

                    try {
                        return connector.beforeClosing();
                    } catch (error) {
                        console.error(error);
                        return true; // Return true on error for the user not to be stuck and to be consistent with the no connector
                    }
                })();

                if (!shouldClose) {
                    return;
                }

                connector.emitter.emit("checkout:closed", { failedSubscriptionId });
            }

            self.setClose();
        },
    }),
};

export const application = createModel()(model);
